import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { 
  FaEdit, FaPrint, FaSearch, 
  FaChevronLeft, FaChevronRight, FaUpload, 
  FaCheckCircle, FaClock, FaExclamationCircle,
  FaCalendarAlt, FaFilter, FaTimes, FaEye,
  FaShieldAlt, FaStore
} from 'react-icons/fa';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';
import Puff from 'react-loading-icons/dist/esm/components/puff';
import { toast } from 'react-toastify';
import FBRInvoiceQRCode from './FBRInvoiceQRCode';

function PushInvoice() {
  const [invoices, setInvoices] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loadingStates, setLoadingStates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fbrInvoiceNumber, setFbrInvoiceNumber] = useState('');
  const [showFbrModal, setShowFbrModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [pushStatus, setPushStatus] = useState({});
  const [fbrDataPreview, setFbrDataPreview] = useState(null);
  const [isLoadingFbrData, setIsLoadingFbrData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationResults, setShowValidationResults] = useState(false);
  
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmInvoiceId, setConfirmInvoiceId] = useState(null);
  const [pushed, setPushed] = useState(null);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [filterDate, setFilterDate] = useState(getTodayDate());

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await axios.get('/api/getsectors');
        if (response.data.sectors && response.data.sectors.length > 0) {
          setSectors(response.data.sectors);
          setSelectedSector(response.data.sectors[0]?.id || '');
        }
      } catch (error) {
        console.error('Error fetching sectors:', error);
        toast.error('Failed to load sectors');
      }
    };
    fetchSectors();
  }, []);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/push/invoices`, {
        params: {
          page: currentPage,
          search: searchQuery,
          date: filterDate,
          sector_id: selectedSector
        }
      });
      
      setInvoices(response.data.invoices.data);
      setPushed(response.data.summary['pushed_to_fbr']);
      setTotalPages(response.data.invoices.last_page);
      setItemsPerPage(response.data.invoices.per_page);
      
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, filterDate, selectedSector]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const openFbrModal = useCallback(async (invoiceId, index) => {
    setConfirmInvoiceId(invoiceId);
    setSelectedIndex(index);
    setShowConfirmDialog(true);
  }, []);

  const confirmPush = useCallback(async () => {
    setShowConfirmDialog(false);
    setSelectedInvoiceId(confirmInvoiceId);
    setShowFbrModal(true);
    setFbrInvoiceNumber('');
    setValidationStatus(null);
    setValidationErrors([]);
    setShowValidationResults(false);
    
    setIsLoadingFbrData(true);
    try {
      const response = await axios.get(`/api/fbr/invoice-data/${confirmInvoiceId}`);
      if (response.data.status === 200) {
        setFbrDataPreview(response.data.data);
        setFbrInvoiceNumber(String(response.data.visitor_id));
      }
    } catch (error) {
      console.error('Error fetching FBR data preview:', error);
      toast.error('Failed to load FBR data preview');
    } finally {
      setIsLoadingFbrData(false);
    }
  }, [confirmInvoiceId]);

  const validateInvoiceWithFBR = useCallback(async () => {
    if (!fbrDataPreview) {
      toast.error('No FBR data available to validate');
      return;
    }

    setIsValidating(true);
    setValidationStatus(null);
    setValidationErrors([]);
    setShowValidationResults(false);
console.log(fbrDataPreview);
    try {
      const response = await axios.post('/api/fbr/validate', fbrDataPreview, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setValidationStatus('success');
        toast.success('Invoice data validated successfully!');
      } else {
        setValidationStatus('error');
        setValidationErrors(response.data.errors || []);
        toast.error(response.data.message || 'Validation failed');
      }
      
      setShowValidationResults(true);
    } catch (error) {
      console.error('Error validating invoice:', error);
      setValidationStatus('error');
      
      const errorMessage = error.response?.data?.message || 'Validation error occurred';
      const errors = error.response?.data?.errors || [errorMessage];
      setValidationErrors(errors);
      
      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  }, [fbrDataPreview]);

  const handleSubmitToFBR = useCallback(async () => {
    if (!fbrInvoiceNumber.trim()) {
      toast.error('Please enter FBR invoice number');
      return;
    }

    if (!fbrDataPreview) {
      toast.error('No FBR data available');
      return;
    }

    if (validationStatus !== 'success') {
      toast.error('Please validate the invoice data first');
      return;
    }

    setIsSubmitting(true);
    setLoadingStates(prev => ({ ...prev, [selectedIndex]: 'submitting' }));

    try {
      const submitData = {
        ...fbrDataPreview,
        fbrInvoiceNumber: fbrInvoiceNumber
      };

      const response = await axios.post('api/fbr/submit', submitData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 200 || response.data.success) {
        toast.success('Invoice submitted to FBR successfully!');
        fetchInvoices();

        setPushStatus(prev => ({ 
          ...prev, 
          [selectedInvoiceId]: { 
            pushed: true, 
            fbrNumber: fbrInvoiceNumber,
            pushedAt: new Date().toLocaleString()
          } 
        }));
        
        setShowFbrModal(false);
        setFbrInvoiceNumber('');
        setSelectedInvoiceId(null);
        setFbrDataPreview(null);
        setValidationStatus(null);
        setValidationErrors([]);
        setShowValidationResults(false);
      
      } else {
        toast.error(response.data.message || 'Failed to submit to FBR');
      }
    } catch (error) {
      console.error('Error submitting to FBR:', error);
      toast.error(error.response?.data?.message || 'Error submitting invoice to FBR');
    } finally {
      setIsSubmitting(false);
      setLoadingStates(prev => ({ ...prev, [selectedIndex]: null }));
    }
  }, [fbrInvoiceNumber, fbrDataPreview, selectedInvoiceId, selectedIndex, fetchInvoices, validationStatus]);

  const handleViewInvoice = useCallback((e, id, index) => {
    e.preventDefault();
    setLoadingStates(prev => ({ ...prev, [index]: 'viewing' }));
    
    const data = { 'id': id };

    axios.get('/sanctum/csrf-cookie').then(() => {
      axios.post('/api/reinvoice/print', data).then(res => {
        const printDatas = {
          letter: res.data.cart || [],
          dates: res.data.dates || '',
          qrcode:res.data.qrcode||'',
          salestax: parseFloat(res.data.salestax) || 0,
          shipping: parseFloat(res.data.shipping) || 0,
          discount: parseFloat(res.data.discount) || 0,
          grandtotal: parseFloat(res.data.grandtotal) || 0,
          invoiceno: res.data.invoiceno || id,
          remainingbill: parseFloat(res.data.remainingbill) || 0,
          customer: res.data.customer || { name: 'N/A', phone: 'N/A' },
          customeraddress: res.data.customeraddress || 'N/A',
          salesman: res.data.salesman || { name: 'N/A' },
          payment: res.data.payment || 'Cash',
          totalpay: parseFloat(res.data.totalpay) || 0,
          pbalance: parseFloat(res.data.pbalance) || 0,
          recovery: parseFloat(res.data.recovery) || 0,
          printingtime: res.data.printingtime || new Date().toLocaleTimeString(),
          manualdate: res.data.manualdate || new Date().toLocaleDateString(),
          detail: res.data.detail || {},
          imagesrc: res.data.imagesrc || '',
          entercash: parseFloat(res.data.entercash) || 0,
          isGift: res.data.isGift || 'No',
          indicator: res.data.indicator || '0'
        };
        
        setPrintData(printDatas);
        setShowPrintModal(true);
        setLoadingStates(prev => ({ ...prev, [index]: null }));
      }).catch(error => {
        console.error('Error fetching print data:', error);
        toast.error('Failed to load invoice details');
        setLoadingStates(prev => ({ ...prev, [index]: null }));
      });
    });
  }, []);

  const closePrintModal = useCallback(() => {
    setShowPrintModal(false);
    setPrintData(null);
  }, []);

  const handlePrintFromModal = useCallback(() => {
    if (printData) {
      const printContent = document.getElementById('print-modal-content');
      if (printContent) {
        const printWindow = window.open('', '_blank', 'height=700,width=700');
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Invoice #${printData.invoiceno}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  padding: 20px;
                  margin: 0;
                }
                @media print {
                  body { padding: 0; }
                }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  }, [printData]);

  const getSerialNumber = useCallback((index) => {
    return (currentPage - 1) * itemsPerPage + index + 1;
  }, [currentPage, itemsPerPage]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  const safeNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const safeString = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.name) return String(value.name);
      if (value.label) return String(value.label);
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderValidationResults = () => {
    if (!showValidationResults) return null;

    return (
      <div style={{ 
        marginTop: '15px', 
        padding: '15px', 
        borderRadius: '8px',
        border: `2px solid ${validationStatus === 'success' ? '#4caf50' : '#f44336'}`,
        background: validationStatus === 'success' ? '#f1f8f1' : '#fef2f2'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {validationStatus === 'success' ? (
            <FaCheckCircle style={{ color: '#4caf50', fontSize: '20px' }} />
          ) : (
            <FaExclamationCircle style={{ color: '#f44336', fontSize: '20px' }} />
          )}
          <span style={{ 
            fontWeight: 'bold',
            color: validationStatus === 'success' ? '#2e7d32' : '#c62828'
          }}>
            {validationStatus === 'success' ? '✓ Validation Passed!' : '✗ Validation Failed'}
          </span>
        </div>
        
        {validationStatus === 'error' && validationErrors.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#c62828', marginBottom: '5px' }}>
              Errors:
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#b71c1c' }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        
        {validationStatus === 'success' && (
          <div style={{ 
            marginTop: '10px', 
            padding: '10px',
            background: '#e8f5e9',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#2e7d32'
          }}>
            <FaCheckCircle style={{ marginRight: '8px' }} />
            All validation checks passed. You can now submit this invoice to FBR.
          </div>
        )}
      </div>
    );
  };

  const renderFbrDataPreview = () => {
    if (!fbrDataPreview) return null;
    
    return (
      <div style={{ 
        marginTop: '15px', 
        padding: '15px', 
        background: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        maxHeight: '300px',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', margin: 0 }}>
            FBR Data Preview:
          </p>
          <button
            onClick={validateInvoiceWithFBR}
            disabled={isValidating}
            style={{
              padding: '6px 15px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: isValidating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: isValidating ? 0.7 : 1
            }}
          >
            {isValidating ? (
              <>
                <Puff height={16} width={16} />
                Validating...
              </>
            ) : (
              <>
                <FaShieldAlt /> Validate
              </>
            )}
          </button>
        </div>
        
        {renderValidationResults()}
      </div>
    );
  };

  const renderInvoiceContent = () => {
    if (!printData) return null;
    
    const items = printData.letter || [];
    
    const total = items.reduce((acc, item) => {
      const qty = safeNumber(item.qty);
      const price = safeNumber(item.unitsaleprice || item.price || item.rate || 0);
      return acc + (qty * price);
    }, 0);
    
    const subtotaldiscount = items.reduce((acc, item) => {
      const value = safeNumber(item.subtotaldiscount);
      return acc + value;
    }, 0);
    
    const totalqty = items.reduce((acc, item) => {
      return acc + safeNumber(item.qty);
    }, 0);
    
    const totalweight = items.reduce((acc, item) => {
      const qty = safeNumber(item.qty);
      const weight = safeNumber(item.weight);
      return acc + (qty * weight);
    }, 0);

    return (
      <div id="print-modal-content" style={{
        fontFamily: 'Times New Roman, serif',
        padding: '20px',
        maxWidth: '800px',
        margin: '0 auto',
        background: 'white'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid #000',
          paddingBottom: '10px',
          marginBottom: '15px'
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
              {safeString(printData.detail?.slogan_heading) || 'SALE INVOICE'}
            </h2>
            <div style={{ fontSize: '12px' }}>
              {safeString(printData.detail?.slogan_subheading)}
            </div>
            <div style={{ fontSize: '11px', marginTop: '2px' }}>
              Ph: {safeString(printData.detail?.phone) || '+92 300-6994971'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {printData.detail?.logo && (
              <img 
                src={`${window.location.origin}/${printData.detail.logo}`}
                alt="Logo"
                style={{ height: '60px', width: '60px', objectFit: 'contain' }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '15px',
          fontSize: '13px',
          borderBottom: '1px solid #ddd',
          paddingBottom: '10px'
        }}>
          <div>
            <div><strong>Invoice No:</strong> {safeString(printData.invoiceno)}</div>
            <div><strong>Date:</strong> {safeString(printData.manualdate)}</div>
            <div><strong>Time:</strong> {safeString(printData.printingtime)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div><strong>Customer:</strong> {safeString(printData.customer?.name)}</div>
            <div><strong>Phone:</strong> {safeString(printData.customer?.phone)}</div>
            <div><strong>Address:</strong> {safeString(printData.customeraddress)}</div>
          </div>
        </div>

        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '12px',
          marginBottom: '15px'
        }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>#</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Item</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Qty</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Weight</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Rate</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => {
                const qty = safeNumber(item.qty);
                const price = safeNumber(item.unitsaleprice || item.price || item.rate || 0);
                const weight = safeNumber(item.weight);
                const itemTotal = qty * price;
                const itemDiscount = safeNumber(item.subtotaldiscount);
                const itemFinalTotal = itemTotal - itemDiscount;
                
                return (
                  <tr key={index}>
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{index + 1}</td>
                    <td style={{ border: '1px solid #ddd', padding: '6px' }}>{safeString(item.name)}</td>
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{qty}</td>
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                      {(qty * weight).toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                      {price.toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                      {itemFinalTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No items found</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="2" style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                Total:
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                {totalqty.toFixed(2)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                {totalweight.toFixed(2)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                {(total - subtotaldiscount).toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>

<div style={{display:'flex',justifyContent:'space-between'}}>


<div style={{textAlign:'left',width:'50%'}}>
  {printData.qrcode && (
    <FBRInvoiceQRCode qrcode={printData.qrcode}/>  // Changed prop name
)}
</div>

        <div style={{ textAlign: 'right',width:'50%', fontSize: '13px', paddingRight: '10px' }}>
          <div><strong>Total Bill:</strong> {safeNumber(printData.grandtotal).toFixed(2)}</div>
          
          {safeNumber(printData.discount) > 0 && (
            <div><strong>Discount:</strong> {safeNumber(printData.discount).toFixed(2)}</div>
          )}
          
          {safeNumber(printData.shipping) > 0 && (
            <div><strong>Shipping:</strong> {safeNumber(printData.shipping).toFixed(2)}</div>
          )}
          
          {safeNumber(printData.discount) > 0 && (
            <div><strong>Total After Discount:</strong> {(safeNumber(printData.grandtotal) + safeNumber(printData.shipping) - safeNumber(printData.discount)).toFixed(2)}</div>
          )}
          
          {safeNumber(printData.salestax) > 0 && (
            <div><strong>Adv.Tax:</strong> {safeNumber(printData.salestax).toFixed(2)}</div>
          )}
          
          {safeNumber(printData.discount) > 0 && (
            <div><strong>Net Amount:</strong> {(safeNumber(printData.remainingbill) + safeNumber(printData.salestax)).toFixed(2)}</div>
          )}
          
          {safeNumber(printData.pbalance) > 0 && (
            <div><strong>Pre Balance:</strong> {safeNumber(printData.pbalance).toFixed(2)}</div>
          )}
          
          <div><strong>Total Bill:</strong> {(safeNumber(printData.pbalance) + safeNumber(printData.remainingbill) + safeNumber(printData.salestax)).toFixed(2)}</div>
          
          <div><strong>Payment:</strong> {(safeNumber(printData.recovery) + safeNumber(printData.payment)).toFixed(2)}</div>
          
          <div style={{ marginTop: '5px', fontSize: '14px', fontWeight: 'bold', borderTop: '2px solid #000', paddingTop: '5px' }}>
            <strong>Current Balance:</strong> {(
              safeNumber(printData.grandtotal) - 
              safeNumber(printData.discount) + 
              safeNumber(printData.salestax) - 
              (safeNumber(printData.payment) + safeNumber(printData.recovery)) + 
              safeNumber(printData.pbalance) + 
              safeNumber(printData.shipping)
            ).toFixed(2)}
          </div>
        </div>

       </div>
      </div>
    );
  };

  return (
    <div className="push-invoice-container">
      <div className="push-invoice-header">
        <h1 className="page-title">Push Invoices to FBR</h1>
        <div className="header-underline"></div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <div className="date-filter-wrapper">
            <FaCalendarAlt className="date-icon" />
            <input
              type="date"
              className="date-picker-input"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              max={getTodayDate()}
            />
          </div>
          
          <div className="sector-filter-wrapper">
            <FaStore className="sector-icon" />
            <select
              className="sector-select"
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
            >
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.id}>
                  {sector.name}
                </option>
              ))}
            </select>
          </div>

          <button className="filter-btn" onClick={fetchInvoices}>
            <FaFilter /> Filter
          </button>
          <button className="filter-btn today-btn" onClick={() => setFilterDate(getTodayDate())}>
            Today
          </button>
        </div>

        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by customer, invoice # or salesman..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="stats-summary">
        <div className="stat-card">
          <div className="stat-icon total"><FaClock /></div>
          <div className="stat-info">
            <span className="stat-label">Total Invoices</span>
            <span className="stat-value">{invoices.length}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending"><FaExclamationCircle /></div>
          <div className="stat-info">
            <span className="stat-label">Pending Push</span>
            <span className="stat-value">
              {invoices.length - (pushed || 0)}
            </span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pushed"><FaCheckCircle /></div>
          <div className="stat-info">
            <span className="stat-label">Pushed to FBR</span>
            <span className="stat-value">
              {pushed || 0}
            </span>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <Table className="modern-table" hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Invoice #</th>
              <th>Customer Name</th>
              <th>Salesman</th>
              <th>Total Amount</th>
              <th>Discount</th>
              <th>Net Due</th>
              <th>Date</th>
              <th>Sector</th>
              <th>Status</th>
              <th>FBR Invoice #</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="12" className="loading-cell">
                  <Puff stroke="#667eea" />
                  <span>Loading invoices...</span>
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan="12" className="no-record-cell">
                  <div className="no-record-content">
                    <FaExclamationCircle className="no-record-icon" />
                    <span>No invoices found for this date and sector</span>
                  </div>
                </td>
              </tr>
            ) : (
              invoices.map((item, index) => {
                const isPushed = pushStatus[item.id]?.pushed || false;
                
                return (
                  <tr key={item.id} className={isPushed ? 'pushed-row' : ''}>
                    <td className="serial">{getSerialNumber(index)}</td>
                    <td className="invoice-no">{item.visitor_id}</td>
                    <td className="customer-name">{item.name}</td>
                    <td>{item.salesman}</td>
                    <td className="amount">{parseFloat(item.total).toFixed(2)}</td>
                    <td>{parseFloat(item.discount).toFixed(2)}</td>
                    <td className={item.due_amount > 0 ? 'due-amount' : ''}>
                      {parseFloat(item.due_amount).toFixed(2)}
                    </td>
                    <td>{formatDate(item.invoice_date)}</td>
                    <td>
                      <span className="sector-badge">
                        {item.sector_name || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${isPushed||item.fbr_invoice_number ? 'status-pushed' : 'status-pending'}`}>
                        {isPushed||item.fbr_invoice_number ? 'Pushed' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      {isPushed ||item.fbr_invoice_number? (
                        <span className="fbr-number">{item.fbr_invoice_number}</span>
                      ) : (
                        <span className="fbr-number-placeholder">{item.fbr_invoice_number}</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <Link target="_blank" className="action-btn edit-btn" to={`/editsaleinvoice/${item.visitor_id}`} title="Edit Invoice">
                        <FaEdit />
                      </Link>
                      <button
                        onClick={(e) => handleViewInvoice(e, item.id, index)}
                        className="action-btn view-btn"
                        disabled={loadingStates[index] === 'viewing'}
                        title="View Invoice"
                      >
                        {loadingStates[index] === 'viewing' ? <Puff width={14} height={14} /> : <FaEye />}
                      </button>
                      <button
                        onClick={() => openFbrModal(item.id, index)}
                        className={`action-btn push-btn ${isPushed||item.fbr_invoice_number ? 'pushed-btn' : ''}`}
                        disabled={loadingStates[index] === 'pushing' || isPushed ||item.fbr_invoice_number|| loadingStates[index] === 'submitting'}
                        title={isPushed ? 'Already Pushed to FBR' : 'Push to FBR'}
                      >
                        {loadingStates[index] === 'submitting' ? 
                          <Puff width={14} height={14} /> : 
                          <FaUpload />
                        }
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-section">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(page => page - 1)}
            className="pagination-btn prev"
          >
            <FaChevronLeft /> Previous
          </button>
          <span className="page-info">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(page => page + 1)}
            className="pagination-btn next"
          >
            Next <FaChevronRight />
          </button>
        </div>
      )}

      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Push to FBR</h3>
              <button className="modal-close" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="confirm-icon">
                <FaExclamationCircle style={{ color: '#ff9800', fontSize: '48px' }} />
              </div>
              <p className="confirm-text">Are you sure you want to push this invoice to FBR?</p>
              <p className="confirm-subtext">Invoice #{confirmInvoiceId} will be sent to FBR for processing.</p>
            </div>
           
            <div className="modal-footer">
              <button className="modal-btn cancel-btn" onClick={() => setShowConfirmDialog(false)}>Cancel</button>
              <button className="modal-btn confirm-btn" onClick={confirmPush}>
                <FaUpload /> Yes, Push to FBR
              </button>
            </div>
          </div>
        </div>
      )}

      {showFbrModal && (
        <div className="modal-overlay" onClick={() => {
          setShowFbrModal(false);
          setValidationStatus(null);
          setValidationErrors([]);
          setShowValidationResults(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FaUpload style={{ marginRight: '10px' }} />
                Push Invoice to FBR
              </h3>
              <button className="modal-close" onClick={() => {
                setShowFbrModal(false);
                setValidationStatus(null);
                setValidationErrors([]);
                setShowValidationResults(false);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                background: '#e3f2fd', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#0d47a1',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaShieldAlt />
                Please validate the invoice data before submitting to FBR
              </div>
              
              <p className="modal-info">Invoice #{selectedInvoiceId}</p>
              
              <div className="modal-input-group" style={{display:'none'}}>
                <label>FBR Invoice Number</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="Enter FBR invoice number..."
                  value={fbrInvoiceNumber}
                  onChange={(e) => setFbrInvoiceNumber(e.target.value)}
                  autoFocus
                />
              </div>
              
              {isLoadingFbrData ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Puff height={30} width={30} />
                  <p style={{ fontSize: '12px', color: '#666' }}>Loading FBR data...</p>
                </div>
              ) : (
                renderFbrDataPreview()
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel-btn" onClick={() => {
                setShowFbrModal(false);
                setValidationStatus(null);
                setValidationErrors([]);
                setShowValidationResults(false);
              }}>Cancel</button>
              <button
                className="modal-btn push-btn-modal"
                onClick={handleSubmitToFBR}
                disabled={!fbrInvoiceNumber.trim() || isLoadingFbrData || isSubmitting || validationStatus !== 'success'}
              >
                {isSubmitting ? (
                  <>
                    <Puff height={20} width={20} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaUpload /> Submit to FBR
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrintModal && printData && (
        <div className="modal-overlay" onClick={closePrintModal}>
          <div className="modal-content print-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header print-modal-header">
              <h3>
                <FaEye style={{ marginRight: '10px' }} />
                Invoice Preview - #{printData.invoiceno}
              </h3>
              <button className="modal-close" onClick={closePrintModal}>×</button>
            </div>
            <div className="modal-body print-modal-body">
              {renderInvoiceContent()}
            </div>
            <div className="modal-footer print-modal-footer">
              <button className="modal-btn cancel-btn" onClick={closePrintModal}>
                <FaTimes /> Close
              </button>
              <button className="modal-btn print-btn-modal" onClick={handlePrintFromModal}>
                <FaPrint /> Print Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .push-invoice-container {
          min-height: 100vh;
          background: linear-gradient(145deg, #f8f9fc 0%, #eef2f7 100%);
          padding: 25px 30px;
        }
        
        .push-invoice-header {
          margin-bottom: 25px;
        }
        
        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: #1e3c72;
          margin: 0 0 10px 0;
        }
        
        .header-underline {
          width: 60px;
          height: 4px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 2px;
        }
        
        .filter-section {
          display: flex;
          gap: 20px;
          margin-bottom: 25px;
          flex-wrap: wrap;
          align-items: center;
        }
        
        .filter-group {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .date-filter-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .date-icon {
          position: absolute;
          left: 12px;
          color: #999;
          font-size: 14px;
        }
        
        .date-picker-input {
          padding: 10px 12px 10px 38px;
          border: 1.5px solid #e0e0e0;
          border-radius: 12px;
          font-size: 14px;
          background: white;
          width: 200px;
          transition: all 0.3s ease;
        }
        
        .date-picker-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
        }
        
        .sector-filter-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .sector-icon {
          position: absolute;
          left: 12px;
          color: #999;
          font-size: 14px;
        }
        
        .sector-select {
          padding: 10px 12px 10px 38px;
          border: 1.5px solid #e0e0e0;
          border-radius: 12px;
          font-size: 14px;
          background: white;
          min-width: 180px;
          transition: all 0.3s ease;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 35px;
        }
        
        .sector-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
        }
        
        .filter-btn {
          padding: 10px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }
        
        .filter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102,126,234,0.3);
        }
        
        .today-btn {
          background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%);
        }
        
        .today-btn:hover {
          background: linear-gradient(135deg, #388e3c 0%, #1b5e20 100%);
        }
        
        .search-wrapper {
          position: relative;
          flex: 1;
          min-width: 200px;
        }
        
        .search-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: 16px;
        }
        
        .search-input {
          width: 100%;
          padding: 10px 15px 10px 45px;
          border: 1.5px solid #e0e0e0;
          border-radius: 12px;
          font-size: 14px;
          transition: all 0.3s ease;
          background: white;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
        }
        
        .stats-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 25px;
        }
        
        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        
        .stat-icon.total {
          background: #e3f2fd;
          color: #2196f3;
        }
        
        .stat-icon.pending {
          background: #fff3e0;
          color: #ff9800;
        }
        
        .stat-icon.pushed {
          background: #e8f5e9;
          color: #4caf50;
        }
        
        .stat-info {
          display: flex;
          flex-direction: column;
        }
        
        .stat-label {
          font-size: 13px;
          color: #999;
        }
        
        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: #1e3c72;
        }
        
        .table-wrapper {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          overflow-x: auto;
          margin-bottom: 25px;
        }
        
        .modern-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 1200px;
        }
        
        .modern-table th {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          padding: 14px 12px;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }
        
        .modern-table td {
          padding: 12px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
          color: #555;
        }
        
        .modern-table tr:hover td {
          background: #f8f9fc;
        }
        
        .pushed-row {
          background: #f0faf0 !important;
        }
        
        .pushed-row td {
          background: #f0faf0 !important;
        }
        
        .serial {
          font-weight: 600;
          color: #667eea;
        }
        
        .invoice-no {
          font-family: monospace;
          font-weight: 600;
          color: #1e3c72;
        }
        
        .customer-name {
          font-weight: 500;
          color: #333;
        }
        
        .amount {
          font-weight: 600;
          color: #28a745;
        }
        
        .due-amount {
          color: #dc3545;
          font-weight: 600;
        }
        
        .sector-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          background: #e8eaf6;
          color: #3f51b5;
          white-space: nowrap;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }
        
        .status-pushed {
          background: #e8f5e9;
          color: #4caf50;
        }
        
        .status-pending {
          background: #fff3e0;
          color: #ff9800;
        }
        
        .fbr-number {
          font-family: monospace;
          font-weight: 600;
          color: #4caf50;
          background: #e8f5e9;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
        }
        
        .fbr-number-placeholder {
          color: #ccc;
        }
        
        .actions-cell {
          display: flex;
          gap: 6px;
          flex-wrap: nowrap;
          white-space: nowrap;
        }
        
        .action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          font-size: 13px;
        }
        
        .edit-btn {
          background: #e3f2fd;
          color: #2196f3;
        }
        
        .edit-btn:hover {
          background: #2196f3;
          color: white;
          transform: translateY(-2px);
        }
        
        .view-btn {
          background: #e8eaf6;
          color: #3f51b5;
        }
        
        .view-btn:hover:not(:disabled) {
          background: #3f51b5;
          color: white;
          transform: translateY(-2px);
        }
        
        .push-btn {
          background: #fff3e0;
          color: #ff9800;
        }
        
        .push-btn:hover:not(:disabled) {
          background: #ff9800;
          color: white;
          transform: translateY(-2px);
        }
        
        .pushed-btn {
          background: #e8f5e9;
          color: #4caf50;
          cursor: default;
        }
        
        .pushed-btn:hover {
          transform: none;
        }
        
        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .loading-cell {
          text-align: center;
          padding: 50px !important;
        }
        
        .loading-cell span {
          display: block;
          margin-top: 10px;
          color: #999;
        }
        
        .no-record-cell {
          text-align: center;
          padding: 40px !important;
        }
        
        .no-record-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          color: #999;
        }
        
        .no-record-icon {
          font-size: 40px;
          color: #ddd;
        }
        
        .pagination-section {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          margin-top: 20px;
        }
        
        .pagination-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1.5px solid #e0e0e0;
          border-radius: 10px;
          color: #1e3c72;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .pagination-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: transparent;
          transform: translateY(-2px);
        }
        
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .page-info {
          font-size: 14px;
          color: #666;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .modal-content {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease;
        }
        
        .print-modal-content {
          max-width: 900px;
          width: 95%;
        }
        
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 25px;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .modal-header h3 {
          margin: 0;
          color: #1e3c72;
          font-size: 18px;
          display: flex;
          align-items: center;
        }
        
        .modal-close {
          background: none;
          border: none;
          font-size: 28px;
          color: #999;
          cursor: pointer;
          transition: color 0.3s ease;
        }
        
        .modal-close:hover {
          color: #333;
        }
        
        .modal-body {
          padding: 25px;
        }
        
        .print-modal-body {
          padding: 10px;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .modal-info {
          color: #666;
          margin-bottom: 20px;
        }
        
        .modal-input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .modal-input-group label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }
        
        .modal-input {
          padding: 12px 15px;
          border: 1.5px solid #e0e0e0;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s ease;
        }
        
        .modal-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
        }
        
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 15px 25px 20px;
          border-top: 1px solid #f0f0f0;
        }
        
        .print-modal-footer {
          flex-wrap: wrap;
        }
        
        .modal-btn {
          padding: 10px 25px;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .cancel-btn {
          background: #f5f5f5;
          color: #666;
        }
        
        .cancel-btn:hover {
          background: #e0e0e0;
        }
        
        .push-btn-modal {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .push-btn-modal:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102,126,234,0.3);
        }
        
        .push-btn-modal:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .print-btn-modal {
          background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%);
          color: white;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .print-btn-modal:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(67,160,71,0.3);
        }
        
        .confirm-dialog {
          max-width: 450px;
        }
        
        .confirm-icon {
          text-align: center;
          margin-bottom: 20px;
        }
        
        .confirm-text {
          text-align: center;
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin-bottom: 10px;
        }
        
        .confirm-subtext {
          text-align: center;
          color: #666;
          font-size: 14px;
        }
        
        .confirm-btn {
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .confirm-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(255,152,0,0.3);
        }
        
        @media (max-width: 1200px) {
          .stats-summary {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        
        @media (max-width: 992px) {
          .filter-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .filter-group {
            width: 100%;
            justify-content: stretch;
          }
          
          .date-filter-wrapper {
            flex: 1;
          }
          
          .date-picker-input {
            width: 100%;
          }
          
          .sector-filter-wrapper {
            flex: 1;
          }
          
          .sector-select {
            width: 100%;
          }
          
          .search-wrapper {
            width: 100%;
          }
        }
        
        @media (max-width: 768px) {
          .push-invoice-container {
            padding: 15px;
          }
          
          .stats-summary {
            grid-template-columns: 1fr;
          }
          
          .page-title {
            font-size: 20px;
          }
          
          .table-wrapper {
            padding: 15px;
          }
          
          .modern-table th,
          .modern-table td {
            padding: 10px 8px;
            font-size: 12px;
          }
          
          .modal-content {
            width: 95%;
            margin: 10px;
          }
        }
        
        @media (max-width: 480px) {
          .filter-group {
            flex-direction: column;
          }
          
          .filter-btn {
            width: 100%;
            justify-content: center;
          }
          
          .modal-footer {
            flex-direction: column;
          }
          
          .modal-btn {
            width: 100%;
            justify-content: center;
          }
          
          .actions-cell {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
}

export default PushInvoice;